import { createI18n } from "vue-i18n";
import { localStorageName } from "@/config/utils";
import en from "./locales/en.json";
import zh from "./locales/zh.json";
import ms from "./locales/ms.json";
import ta from "./locales/ta.json";
import th from "./locales/th.json";
import ja from "./locales/ja.json";
import ko from "./locales/ko.json";
import vi from "./locales/vi.json";

// MBR093: the member app shows ONE of 8 languages, picked from the Home top-bar
// language sheet. Only UI copy is translated — server data (reward / store names,
// merchant content, API messages) stays as returned. The order and labels below
// drive the sheet; each label is written in its own language, `name` in English.
export const LANGUAGES = [
  { code: "en", label: "English", name: "English" },
  { code: "zh", label: "中文", name: "Chinese" },
  { code: "ms", label: "Bahasa Melayu", name: "Malay" },
  { code: "ta", label: "தமிழ்", name: "Tamil" },
  { code: "th", label: "ไทย", name: "Thai" },
  { code: "ja", label: "日本語", name: "Japanese" },
  { code: "ko", label: "한국어", name: "Korean" },
  { code: "vi", label: "Tiếng Việt", name: "Vietnamese" },
];
export const SUPPORTED_LOCALES = LANGUAGES.map((l) => l.code);
export const DEFAULT_LOCALE = "en";
const LOCALE_STORAGE_KEY = "MBR_LOCALE";

// Missing keys fall back to English, so a string not yet translated still reads.
const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en, zh, ms, ta, th, ja, ko, vi },
});

function applyLocale(locale) {
  i18n.global.locale.value = locale;
  document.documentElement.setAttribute("lang", locale);
}

// Persisted per merchant (localStorageName appends the merchant code), so call it
// once the merchant code is in the store — the router guard does.
export function restoreLocale() {
  let stored = null;
  try {
    stored = localStorage.getItem(localStorageName(LOCALE_STORAGE_KEY));
  } catch (e) {
    // ignore storage errors
  }
  applyLocale(SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE);
}

export function setLocale(locale) {
  const next = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
  applyLocale(next);
  try {
    localStorage.setItem(localStorageName(LOCALE_STORAGE_KEY), next);
  } catch (e) {
    // ignore storage errors
  }
  return next;
}

// Dates: English keeps each screen's existing hand-built format; the other languages go through
// Intl (Thai forced to the Gregorian calendar — th-TH defaults to the Buddhist year).
const INTL_LOCALES = {
  en: "en-US",
  zh: "zh-CN",
  ms: "ms-MY",
  ta: "ta-SG",
  th: "th-TH-u-ca-gregory",
  ja: "ja-JP",
  ko: "ko-KR",
  vi: "vi-VN",
};

export function isEnglish() {
  return i18n.global.locale.value === DEFAULT_LOCALE;
}

// BCP 47 tag for Intl and the datepicker's `locale` prop
export function dateLocale() {
  return INTL_LOCALES[i18n.global.locale.value] || INTL_LOCALES.en;
}

export function formatDate(date, options) {
  return new Intl.DateTimeFormat(dateLocale(), options).format(date);
}

export default i18n;
